/**
 * Full-viewport branded boot screen, held up while the silent session restore
 * decides which surface the visitor belongs on.
 *
 * It exists because both alternatives leak. Rendering the app chrome around a
 * spinner shows an anonymous visitor a signed-out dashboard they are about to be
 * redirected away from; a bare progress bar on an empty canvas reads as a broken
 * white screen. Neither states the truth, which is "deciding".
 *
 * Deliberately wordless: this package has no i18n or operator-branding context,
 * and the mark alone carries the identity. It is `position: fixed` so it covers
 * the rail and app bar when a guard renders it from inside the layout.
 *
 * The pre-hydration bar in each app's index.html is the other half of this — it
 * paints the same canvas and bar before the bundle has parsed, so this component
 * only adds the mark on top rather than replacing a blank page.
 */
import { useEffect } from 'react';
import LinearProgress from '@mui/material/LinearProgress';
import { BrandMark } from './BrandMark';

export interface AppSplashProps {
  /** Accessible status text. Announced, never drawn. */
  label?: string;
}

/** Element id of the pre-hydration splash both apps put in their index.html. */
export const BOOT_SPLASH_ID = 'boot-splash';

/**
 * Drops the pre-hydration splash once React has committed. Call it from the root
 * component of an app whose index.html carries one.
 *
 * It is an effect and not a statement next to `render()`: render only schedules
 * the work, so removing the cover there would expose the blank page this whole
 * mechanism exists to hide. Removing an already-detached node is a no-op, which
 * is what makes StrictMode's double-invoked effects harmless here.
 */
export function useBootSplashTeardown(): void {
  useEffect(() => {
    document.getElementById(BOOT_SPLASH_ID)?.remove();
  }, []);
}

export function AppSplash({ label = 'Loading' }: AppSplashProps) {
  return (
    <div
      role="status"
      aria-busy="true"
      style={{
        position: 'fixed',
        inset: 0,
        // Above the rail (1200) and modals (1300); below the tooltip layer.
        zIndex: 1400,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 20,
        background: 'var(--fms-color-bg)',
      }}
    >
      <BrandMark size={56} />
      {/* Same 160px bar the index.html splash draws, in the same place, so the
          handover from pre-hydration HTML to React is invisible. */}
      <div style={{ width: 160 }}>
        <LinearProgress aria-hidden="true" />
      </div>
      <span
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          overflow: 'hidden',
          clipPath: 'inset(50%)',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </span>
    </div>
  );
}
